import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MediaFromText, MediaUpdate, Voice, VoiceKeySave, VoiceSettingsSave, VoiceStatus } from '@arcnet/contracts';
import { api } from './client.js';

/**
 * A recording's words, and recordings made from text (owner, 7 October 2026; migration 0160).
 * Everything that changes a recording also refreshes the media list, which every recording picker
 * reads.
 */

export const voiceKeys = {
  status: (t: string) => ['voice', t] as const,
  voices: (t: string) => ['voice', t, 'voices'] as const,
};
const mediaKey = (t: string) => ['media', t] as const;

export const useVoice = (t: string) =>
  useQuery({
    queryKey: voiceKeys.status(t),
    queryFn: () => api.get<VoiceStatus>(`/v1/tenants/${t}/voice`),
    enabled: !!t,
  });

/** The voices the key in use offers. Asked only when somebody is choosing one. */
export const useVoices = (t: string, enabled: boolean) =>
  useQuery({
    queryKey: voiceKeys.voices(t),
    queryFn: () => api.get<{ data: Voice[] }>(`/v1/tenants/${t}/voice/voices`),
    enabled: !!t && enabled,
    staleTime: 10 * 60_000,
  });

export function useSaveVoiceSettings(t: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VoiceSettingsSave) => api.put<{ ok: true }>(`/v1/tenants/${t}/voice/settings`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: voiceKeys.status(t) }); },
  });
}

export function useSaveVoiceKey(t: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: VoiceKeySave) => api.put<{ ok: true }>(`/v1/tenants/${t}/voice/key`, body),
    meta: { toast: false },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['voice', t] }); },
  });
}

export function useRemoveVoiceKey(t: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete(`/v1/tenants/${t}/voice/key`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['voice', t] }); },
  });
}

/** A recording made from text: the new file, which every picker then lists. */
export function useMakeRecording(t: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MediaFromText) => api.post<{ id: string; name: string }>(`/v1/tenants/${t}/media/from-text`, body),
    // The form says what stopped it (a cap, no voice), beside the button.
    meta: { toast: false },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: mediaKey(t) });
      void qc.invalidateQueries({ queryKey: voiceKeys.status(t) });
    },
  });
}

export function useUpdateMedia(t: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: MediaUpdate & { id: string }) => api.patch(`/v1/tenants/${t}/media/${id}`, body),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: mediaKey(t) }); },
  });
}

/** What a recording says, heard from the recording with the account's transcription key. */
export function useTranscribeMedia(t: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/v1/tenants/${t}/media/${id}/transcribe`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: mediaKey(t) }); },
  });
}
